import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useInViewport } from 'ahooks';

/**
 * @param forceSticky 强制保持 sticky（position:fixed）状态。
 *   全屏模式下，缩放产生水平滚动条会导致可视区域高度缩小，
 *   IntersectionObserver 可能误判 sentinel 重新可见并撤销 position:fixed，
 *   工具栏随之回到文档流位置（已被滚动出视口而消失）。
 *   传入 true 可避免此误判，始终保持固定在顶部。
 */
function useSticky(elementRef: RefObject<HTMLElement>, forceSticky?: boolean) {
    const [isSticky, setSticky] = useState(false);
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    // 使用 IntersectionObserver（ahooks/useInViewport）监听哨兵元素是否仍在可视区
    // - 当哨兵滚出可视区：认为需要吸顶
    // - 该方式能覆盖“容器滚动但 window 不滚动”的场景（不会依赖 window.scroll）
    const [sentinelInViewport] = useInViewport(sentinelRef);

    const canUseIntersectionObserver = useMemo(() => {
        return typeof window !== 'undefined' && typeof (window as any).IntersectionObserver !== 'undefined';
    }, []);

    useEffect(() => {
        // 在 toolbar 前插入一个哨兵元素，用来判断是否“滚过了 toolbar 位置”
        const el = elementRef.current;
        if (!el) {
            return;
        }
        if (sentinelRef.current) {
            return;
        }
        const sentinel = document.createElement('div');
        sentinel.style.cssText = 'position: relative; width: 1px; height: 1px;';
        el.parentElement?.insertBefore(sentinel, el);
        sentinelRef.current = sentinel;

        return () => {
            // 清理哨兵
            if (sentinelRef.current?.parentElement) {
                sentinelRef.current.parentElement.removeChild(sentinelRef.current);
            }
            sentinelRef.current = null;
        };
    }, [elementRef]);

    // 降级方案：在不支持 IntersectionObserver 的环境下，回退到 scroll 计算
    useEffect(() => {
        if (canUseIntersectionObserver) {
            return;
        }
        const updatePosition = () => {
            const el = elementRef.current;
            if (!el) {
                return;
            }
            const elementTop = el.getBoundingClientRect().top || 0;
            // 只要元素顶部到达/越过视口顶部，就认为 sticky
            setSticky(elementTop <= 0);
        };
        window.addEventListener('scroll', updatePosition, { passive: true });
        updatePosition();
        return () => {
            window.removeEventListener('scroll', updatePosition as any);
        };
    }, [canUseIntersectionObserver, elementRef]);

    // 主路径：IntersectionObserver 驱动 sticky
    useEffect(() => {
        if (!canUseIntersectionObserver) {
            return;
        }
        // sentinel 在视口内 => 未吸顶；不在视口内 => 吸顶
        setSticky(!sentinelInViewport);
    }, [canUseIntersectionObserver, sentinelInViewport]);

    // 设定元素的样式
    // forceSticky 时始终应用 fixed，忽略 isSticky 的翻转（防止水平滚动条误触发 sentinel）
    useEffect(() => {
        if (!elementRef.current) {
            return;
        }

        if (isSticky || forceSticky) {
            elementRef.current.style.position = 'fixed';
            elementRef.current.style.top = '0';
            elementRef.current.style.margin = '0';
            elementRef.current.style.zIndex = '1000';
        } else {
            elementRef.current.style.position = '';
            elementRef.current.style.top = '';
            elementRef.current.style.margin = '';
            elementRef.current.style.zIndex = '';
        }
    }, [elementRef, isSticky, forceSticky]);
    return isSticky || !!forceSticky;
}

export default useSticky;
